import { AnimatePresence, motion } from "framer-motion";
import { Apple, Bot, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePlatform } from "@/context/PlatformContext";
import { PLATFORM_IDS, PLATFORMS } from "@/data/platform";

/**
 * Always-accessible floating platform switcher. Lives in the bottom-right
 * corner on every page. Starts collapsed as a small branded disc showing
 * the current platform; tap to expand into a two-option pill so users can
 * flip anytime without scrolling back to the hero.
 *
 * On the /shop route (mobile) the sticky Add-to-Cart bar sits at the bottom,
 * so the FAB auto-hides on mobile for /shop to avoid overlap. It stays
 * visible on desktop where there's no sticky bar.
 */
const FloatingPlatformSwitcher = () => {
  const { platform, setPlatform, config } = usePlatform();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onShop = pathname === "/shop" || pathname.startsWith("/shop/");

  // Close on escape
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const Icon = platform === "apple" ? Apple : Bot;

  return (
    <div
      data-testid="floating-platform-switcher"
      className={`fixed right-4 z-40 flex flex-col items-end gap-2 bottom-[calc(5rem+env(safe-area-inset-bottom))] lg:bottom-6 lg:right-6 lg:z-[60] ${onShop ? "hidden lg:flex" : ""}`}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            className="flex flex-col items-stretch gap-2 rounded-2xl border border-black/10 bg-white/95 p-2 shadow-lift backdrop-blur-xl"
          >
            <p className="px-3 pt-1 text-[9px] font-black uppercase tracking-[0.22em] text-tracksy-sub">
              Choose your platform
            </p>
            {PLATFORM_IDS.map((id) => {
              const p = PLATFORMS[id];
              const isActive = platform === id;
              const PIcon = id === "apple" ? Apple : Bot;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setPlatform(id); setOpen(false); }}
                  data-testid={`floating-platform-${id}`}
                  className={`flex items-center justify-between gap-6 rounded-xl px-3 py-2.5 text-left transition-colors ${
                    isActive ? "text-white" : "text-tracksy-ink hover:bg-black/5"
                  }`}
                  style={isActive ? { background: p.accent } : undefined}
                >
                  <span className="flex items-center gap-2">
                    <PIcon className="h-4 w-4" />
                    <span className="text-xs font-black uppercase tracking-widest">{p.short}</span>
                  </span>
                  <span className={`text-[9px] font-black uppercase tracking-[0.18em] ${isActive ? "text-white/80" : "text-tracksy-sub"}`}>
                    {p.networkShort}
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        data-testid="floating-platform-toggle"
        onClick={() => setOpen((o) => !o)}
        whileTap={{ scale: 0.94 }}
        whileHover={{ scale: 1.05 }}
        aria-expanded={open}
        aria-label={`Switch platform (currently ${config.short})`}
        className="group relative flex h-11 w-11 items-center justify-center rounded-full text-white shadow-glow lg:h-14 lg:w-14"
        style={{ background: "var(--brand-accent)" }}
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={open ? "close" : platform}
            initial={{ opacity: 0, rotate: -20 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: 20 }}
            transition={{ duration: 0.22 }}
            className="inline-flex"
          >
            {open ? <X className="h-4 w-4 lg:h-5 lg:w-5" /> : <Icon className="h-4 w-4 lg:h-5 lg:w-5" />}
          </motion.span>
        </AnimatePresence>
        {/* Micro label — hidden on mobile for cleanliness */}
        <span className="pointer-events-none absolute right-[72px] hidden rounded-full bg-white/95 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-tracksy-ink shadow-subtle group-hover:inline lg:inline">
          {config.short} · {config.networkShort}
        </span>
      </motion.button>
    </div>
  );
};

export default FloatingPlatformSwitcher;
